import React from 'react';

const SLA_LABELS = { 0: 'Basic', 1: 'Professional', 2: 'Enterprise', 'Basic': 'Basic', 'Professional': 'Professional', 'Enterprise': 'Enterprise' };
const SLA_COLORS = { 0: 'text-slate-400', 1: 'text-blue-400', 2: 'text-purple-400', 'Basic': 'text-slate-400', 'Professional': 'text-blue-400', 'Enterprise': 'text-purple-400' };

// Format price from USDC lamports (6 decimals) to dollars
function formatPrice(lamports) {
  if (typeof lamports === 'number' && lamports > 1) return `$${(lamports / 1_000_000).toFixed(4)}`;
  return `$${lamports}`;
}

// Format uptime from bps (9900) or percent (99.0)
function formatUptime(val) {
  if (!val) return '95%';
  if (val > 100) return `${(val / 100).toFixed(1)}%`; // bps
  return `${val}%`;
}

export default function ProviderGrid({ providers, onSelect }) {
  if (!providers || providers.length === 0) {
    return (
      <div className="text-center py-16 text-slate-500 text-sm">
        No providers found. Try different search tags.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {providers.map((p) => (
        <div
          key={p.wallet || p.provider}
          onClick={() => onSelect(p)}
          className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 cursor-pointer hover:border-purple-500/30 transition-all group"
        >
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-white group-hover:text-purple-300 transition-colors">{p.name}</h3>
              <p className={`text-[10px] ${SLA_COLORS[p.sla_tier ?? p.sla_name ?? 0] || 'text-slate-400'}`}>
                {SLA_LABELS[p.sla_tier ?? p.sla_name ?? 0] || 'Basic'} SLA
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1">
                <span className="text-xs font-mono text-emerald-400">{p.reputation_active || 50}</span>
                <span className="text-[10px] text-slate-500">/100</span>
              </div>
              <p className="text-[10px] text-slate-500">reputation</p>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1 mt-3">
            {(p.tags || []).slice(0, 5).map((tag, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
                {tag}
              </span>
            ))}
            {(p.tags || []).length > 5 && (
              <span className="text-[10px] text-slate-600">+{p.tags.length - 5}</span>
            )}
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-800">
            <div>
              <p className="text-[10px] text-slate-500">Price/call</p>
              <p className="text-sm font-mono text-white">{formatPrice(p.price_per_call)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Total calls</p>
              <p className="text-sm font-mono text-white">{(p.total_calls || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Uptime</p>
              <p className="text-sm font-mono text-white">{formatUptime(p.promised_uptime_bps || p.promised_uptime_pct)}</p>
            </div>
          </div>

          {/* Free tier badge */}
          {p.free_tier_daily > 0 && (
            <div className="mt-2 text-[10px] text-emerald-400">
              {p.free_tier_daily} free calls/day
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
